import { Alert, Box, Button, Checkbox, Chip, Divider, FormControlLabel, Grid, List, ListItemButton, ListItemText, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { errorMessage } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { EmptyState, ErrorState, LoadingState, PageHeader, PermissionGate } from "../components";
import help from "./HELP.md?raw";
import { useCatalog, useRoleAction, useRoles, useSaveRole, type Role } from "./useData";

export function Roles() {
  const { hasPermission } = useAuth();
  const roles = useRoles();
  const catalog = useCatalog();
  const save = useSaveRole();
  const action = useRoleAction();
  const [selectedId, setSelectedId] = useState<string | "new" | null>(null);
  const [draft, setDraft] = useState<{ name: string; description: string; codes: Set<string> }>({ name: "", description: "", codes: new Set() });
  const [error, setError] = useState<string | null>(null);
  const selected: Role | undefined = useMemo(() => roles.data?.find((r) => r.id === selectedId), [roles.data, selectedId]);

  useEffect(() => {
    if (selectedId === "new") setDraft({ name: "", description: "", codes: new Set() });
    else if (selected) setDraft({ name: selected.name, description: selected.description, codes: new Set(selected.permission_codes) });
  }, [selectedId, selected]);

  const readOnly = !!selected?.is_system || !hasPermission("accounts.role.change");
  const toggle = (codes: string[], on: boolean) => setDraft((d) => { const next = new Set(d.codes); codes.forEach((c) => on ? next.add(c) : next.delete(c)); return { ...d, codes: next }; });

  async function onSave() {
    setError(null);
    try {
      const saved = await save.mutateAsync({ id: selectedId === "new" ? undefined : selectedId!, name: draft.name, description: draft.description, permission_codes: [...draft.codes].sort() });
      setSelectedId(saved.id);
    } catch (err) { setError(errorMessage(err)); }
  }

  if (roles.isLoading || catalog.isLoading) return <LoadingState />;
  if (roles.isError) return <ErrorState message={roles.error.message} onRetry={() => void roles.refetch()} />;
  return (
    <>
      <PageHeader help={help} title="Roles & permissions" subtitle="System roles are maintained automatically; copy one to customise it." crumbs={[{ label: "Settings" }, { label: "Roles" }]}
        actions={<PermissionGate permission="accounts.role.create"><Button variant="contained" onClick={() => setSelectedId("new")}>New role</Button></PermissionGate>} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 3 }}>
          <List dense sx={{ bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider" }}>
            {roles.data?.map((r) => (
              <ListItemButton key={r.id} selected={r.id === selectedId} onClick={() => setSelectedId(r.id)}>
                <ListItemText primary={<>{r.name} {r.is_system && <Chip label="system" size="small" sx={{ ml: 0.5 }} />}</>} secondary={`${r.member_count} member${r.member_count === 1 ? "" : "s"} · ${r.permission_codes.length} permissions`} />
              </ListItemButton>
            ))}
          </List>
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          {!selectedId ? <EmptyState title="Select a role" description="Pick a role on the left to see its permissions." /> : (
            <Box sx={{ bgcolor: "background.paper", borderRadius: 1, border: 1, borderColor: "divider", p: 2 }}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {selected?.is_system && <Alert severity="info" sx={{ mb: 2 }}>System roles follow the product's permission catalog and cannot be edited. Use <b>Duplicate</b> to create an editable copy.</Alert>}
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField label="Role name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} disabled={readOnly} sx={{ maxWidth: 320 }} />
                <TextField label="Description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} disabled={!hasPermission("accounts.role.change")} />
              </Stack>
              {catalog.data?.map((group) => {
                const groupCodes = group.features.flatMap((f) => f.permissions.map((p) => p.code));
                const allOn = groupCodes.every((c) => draft.codes.has(c));
                return (
                  <Box key={group.spoke} sx={{ mb: 2 }}>
                    <FormControlLabel label={<Typography variant="subtitle2">{group.title}</Typography>}
                      control={<Checkbox checked={allOn} indeterminate={!allOn && groupCodes.some((c) => draft.codes.has(c))} disabled={readOnly} onChange={(e) => toggle(groupCodes, e.target.checked)} />} />
                    <Grid container spacing={1} sx={{ pl: 4 }}>
                      {group.features.map((f) => (
                        <Grid key={f.feature} size={{ xs: 12, sm: 6, lg: 4 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>{f.feature.replace(/_/g, " ")}</Typography>
                          <Stack>
                            {f.permissions.map((p) => (
                              <FormControlLabel key={p.code} sx={{ ml: 0 }} label={<Typography variant="body2">{p.label}</Typography>}
                                control={<Checkbox size="small" checked={draft.codes.has(p.code)} disabled={readOnly} onChange={(e) => toggle([p.code], e.target.checked)} />} />
                            ))}
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>
                    <Divider sx={{ mt: 1 }} />
                  </Box>
                );
              })}
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                {selected && <PermissionGate permission="accounts.role.create"><Button onClick={() => action.mutate({ id: selected.id, action: "duplicate", name: `${selected.name} (copy)` })}>Duplicate</Button></PermissionGate>}
                {selected && !selected.is_system && <PermissionGate permission="accounts.role.delete"><Button color="error" onClick={() => { if (confirm(`Delete role ${selected.name}?`)) { action.mutate({ id: selected.id, action: "delete" }); setSelectedId(null); } }}>Delete</Button></PermissionGate>}
                {(!selected?.is_system || hasPermission("accounts.role.change")) && <Button variant="contained" disabled={save.isPending || !draft.name || (selected?.is_system && draft.description === selected.description)} onClick={onSave}>Save</Button>}
              </Stack>
              {action.isError && <Alert severity="error" sx={{ mt: 1 }}>{action.error.message}</Alert>}
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
}
